import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import categoryService from '../services/categoryService';
import productService from '../services/productService';
import { 
  Typography, 
  Paper, 
  Box
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SettingsIcon from '@mui/icons-material/Settings';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => {
  return (
    <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            backgroundColor: `${color}20`, // Couleur avec opacité
            borderRadius: '50%',
            width: 60,
            height: 60,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mr: 2
          }}
        >
          <Box sx={{ color }}>
            {icon}
          </Box>
        </Box>
        <Box>
          <Typography variant="h5" component="div">
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const Dashboard: React.FC = () => {
  const [categoryCount, setCategoryCount] = useState<number>(0);
  const [productCount, setProductCount] = useState<number>(0);
  const [customizationCount, setCustomizationCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const [categories, productsCount, customizationsCount] = await Promise.all([
          categoryService.getCategories(),
          productService.getProductCount(),
          productService.getCustomizationRulesCount(),
        ]);
        setCategoryCount(categories.length);
        setProductCount(productsCount);
        setCustomizationCount(customizationsCount);

      } catch (err) {
        console.error('Erreur lors du chargement des statistiques:', err);
        setError('Erreur lors du chargement des données. Veuillez rafraîchir la page.');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  return (
    <Layout title="Tableau de bord">
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Bienvenue dans l'administration Step Burger
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Gérez vos catégories, produits et règles de personnalisation à partir de ce tableau de bord.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3
        }}
      >
        <Box sx={{ flex: '1 1 300px' }}>
          <StatCard
            title="Catégories"
            value={loading ? '...' : categoryCount}
            icon={<CategoryIcon sx={{ fontSize: 40 }} />}
            color="#4CAF50"
          />
        </Box>
        <Box sx={{ flex: '1 1 300px' }}>
          <StatCard
            title="Produits"
            value={loading ? '...' : productCount}
            icon={<RestaurantMenuIcon sx={{ fontSize: 40 }} />}
            color="#2196F3"
          />
        </Box>
        <Box sx={{ flex: '1 1 300px' }}>
          <StatCard
            title="Règles de Customisation"
            value={loading ? '...' : customizationCount}
            icon={<SettingsIcon sx={{ fontSize: 40 }} />}
            color="#FF9800"
          />
        </Box>
      </Box>

      <Box mt={6}>
        <Typography variant="h5" gutterBottom>
          Comment utiliser cette application
        </Typography>
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="body1" paragraph>
            1. <strong>Catégories</strong> - Commencez par créer des catégories de produits avec leurs images.
          </Typography>
          <Typography variant="body1" paragraph>
            2. <strong>Produits</strong> - Ajoutez des produits dans les catégories avec leurs détails et images.
          </Typography>
          <Typography variant="body1" paragraph>
            3. <strong>Customisations</strong> - Configurez les règles de personnalisation pour chaque catégorie de produits.
          </Typography>
        </Paper>
      </Box>
    </Layout>
  );
};

export default Dashboard;
